'use client';

import type { Cart } from '@repo/shopify';
import { useCallback, useState } from 'react';
import { type RewardClaimLineInput, useCartActions } from '@/components/cart/cart-provider';
import { KIT_CONTENTS_ATTRIBUTE_KEY, KIT_CONTENTS_DISPLAY_ATTRIBUTE_KEY } from '@/components/cart/reward-line-utils';
import type { GiftSelection } from '@/components/product/gift-variant-modal';
import { buildGiftExtraLines, buildGiftSelections } from '@/components/product/subscribe-save-gift-lines';
import type { HydrateBundleFlavor, HydrateBundleKitProduct } from '@/lib/insider-bundle-config';
import { getSellingPlanLabel, getSellingPlanUnitPrice } from '@/lib/selling-plans';
import type { SubscribeSaveConfig } from '@/lib/subscribe-save-config';
import type { BundleFrequencyOption, BundleSlots } from './hydrate-bundle-math';
interface AddBundleInput {
  slots: BundleSlots;
  /** Selected delivery frequency; null = one-time purchase. */
  frequency: BundleFrequencyOption | null;
}

/** Picked flavors aggregated for the kit-contents attributes, slot order kept. */
function aggregateSelections(slots: BundleSlots, byHandle: Map<string, HydrateBundleFlavor>) {
  const selections: {
    flavor: HydrateBundleFlavor;
    quantity: number;
  }[] = [];
  for (const handle of slots) {
    const flavor = handle ? byHandle.get(handle) : null;
    if (!flavor) return null;
    const existing = selections.find(entry => entry.flavor.handle === flavor.handle);
    if (existing) {
      existing.quantity += 1;
    } else {
      selections.push({
        flavor,
        quantity: 1
      });
    }
  }
  return selections;
}

/**
 * Adds the Crew Pack as ONE cart line: the kit product's variant, on the kit
 * product's selling plan when subscribing, with the picked flavors recorded as
 * line attributes — `_kit_contents` (JSON, the WMS contract) plus a
 * human-readable `Contents`. Shopify does the real pricing (kit variant price
 * one-time, its Recharge plan per delivery); the WMS explodes the kit SKU into
 * bag picks from the contents attribute, and Recharge carries the attributes
 * onto every renewal order. Subscription gifts ride along in the same batched
 * `claimRewardItems` mutation (the cart provider rejects concurrent
 * mutations, so one call is mandatory).
 */
export function useHydrateBundleAdd({
  flavors,
  kitProduct,
  subscribeSaveConfig,
  onGiftSelections
}: {
  flavors: HydrateBundleFlavor[];
  kitProduct: HydrateBundleKitProduct | null;
  subscribeSaveConfig: SubscribeSaveConfig;
  onGiftSelections?: (selections: GiftSelection[]) => void;
}) {
  const {
    claimRewardItems,
    getCart
  } = useCartActions();
  const [isAdding, setIsAdding] = useState(false);
  const addBundle = useCallback(
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: one mutation composes the kit line, its contents attributes, and gift lines with per-step validation
  async ({
    slots,
    frequency
  }: AddBundleInput): Promise<Cart | null> => {
    if (isAdding || !kitProduct) return null;
    const byHandle = new Map(flavors.map(flavor => [flavor.handle, flavor]));
    const selections = aggregateSelections(slots, byHandle);
    if (!selections || selections.length === 0) return null;
    const plan = frequency?.plan ?? null;
    const attributes = [{
      key: KIT_CONTENTS_ATTRIBUTE_KEY,
      value: JSON.stringify(selections.map(({
        flavor: flavor_0,
        quantity
      }) => ({
        sku: flavor_0.sku,
        handle: flavor_0.handle,
        quantity
      })))
    }, {
      key: KIT_CONTENTS_DISPLAY_ATTRIBUTE_KEY,
      value: selections.map(({
        flavor: flavor_1,
        quantity: quantity_0
      }) => `${quantity_0} × ${flavor_1.label}`).join(', ')
    }];
    const lines: RewardClaimLineInput[] = [{
      merchandiseId: kitProduct.merchandiseId,
      quantity: 1,
      attributes,
      // name = derived label so the optimistic drawer line shows the real
      // frequency (Recharge plan names can be wrong, see getSellingPlanLabel).
      sellingPlan: plan ? {
        id: plan.id,
        name: getSellingPlanLabel(plan)
      } : undefined,
      productInfo: {
        productId: kitProduct.productId,
        title: kitProduct.title,
        handle: kitProduct.handle,
        vendor: kitProduct.vendor,
        productType: kitProduct.productType,
        image: kitProduct.image ? {
          url: kitProduct.image.url,
          altText: kitProduct.image.altText
        } : null,
        sku: kitProduct.sku,
        price: plan ? getSellingPlanUnitPrice(kitProduct.price, plan) : kitProduct.price,
        attributes,
        optimisticLineKey: `kit:${kitProduct.handle}`
      }
    }];

    // Gifts come with the ORDER, not with every subscription: only the
    // first subscription line in the cart brings its gifts along.
    const cart = getCart();
    const cartHasSubscription = !!cart?.lines.edges.some(edge => edge.node.sellingPlanAllocation);
    const giftsApply = !!frequency && !cartHasSubscription;
    if (giftsApply) {
      const currencyCode = kitProduct.price.currencyCode;
      for (const gift of buildGiftExtraLines(subscribeSaveConfig.gifts, cart, currencyCode)) {
        lines.push({
          ...gift,
          // claimRewardItems sends line-level attributes (addItem reads
          // them off productInfo instead).
          attributes: gift.productInfo.attributes
        });
      }
    }
    setIsAdding(true);
    try {
      const nextCart = await claimRewardItems(lines, {
        analyticsSource: 'hydrate_bundle'
      });
      if (nextCart && giftsApply) {
        onGiftSelections?.(buildGiftSelections(subscribeSaveConfig.gifts, nextCart));
      }
      return nextCart;
    } finally {
      setIsAdding(false);
    }
  }, [isAdding, flavors, kitProduct, getCart, claimRewardItems, subscribeSaveConfig.gifts, onGiftSelections]);
  return {
    addBundle,
    isAdding
  };
}